import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import OnBoardingForm from "pages/EmployeePortal/Pages/OnBoardingForm/OnBoardingFrom";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import { getToken } from "helpers/Tokens";
import { USER_ROLE } from "Constants/constant";
import {
  PERMISSION_KEYS,
  PERMISSION_KEYS_CHILDREN,
} from "store/constants";
import { getUser } from "store/selector/auth.selector";
import { DynamicRoutes } from "./Routes";
import ErrorPage2FA from "components/common/404Page2FA/404Page2FA";
import { getUserTypeFromLC, parseBooleanVal } from "Utils/utils";
import { resolveRouteUserType } from "pages/PlatformAdmin/Ghost/ghostSession.utils";
import { isGhostSessionActive } from "context/platformAdminContext.utils";
import ErrorPage from "components/common/404Page/404Page";
import FANotEnable from "../pages/Authentication/TwoFactorAuth/TwoFANotEnabled/index";
import SignIn from "pages/Authentication/SignIn/SignIn";
import FAEnabled from "../pages/Authentication/TwoFactorAuth/TwoFAEnabled/index";
import FANotEnabledOTP from "../pages/Authentication/TwoFactorAuth/TwoFANotEnabledOTP/index";
import OTPSelectionScreen from "../pages/Authentication/TwoFactorAuth/OTPSelectionScreen/index";
import FANotEnabledOTPVarified from "../pages/Authentication/TwoFactorAuth/TwoFANotEnabledOTPVarified/index";
import { isCareWatchVisible, SP_SETTINGS_AI_UPDATED_EVENT } from "../helpers/spAiFeatureAccess";
import {
  checkSubscriptionStatus,
  isOverParticipantLimit,
  roleAccess,
  canAccessPayrollSettings,
  getSpPermissionStatus,
  hasSpAdminLevelAccess,
  isTrueSpOwner,
  shouldLockToSubscriptionPlansForUser,
  shouldShowSubscriptionBannerForUser,
  usePermissionsVersion,
} from "helpers/RolesPermission";
import PermissionDenied from "components/common/404Permission/404Permission";
import SubscriptionDetailsDashboard from "pages/Stripe/Subscription/Dashboard";
import PricingTable from "pages/Stripe/Pricing/PricingTable";
import FeatureOfTheWeekLanding from "pages/Public/FeatureOfTheWeekLanding";
import {
  FEATURE_OF_THE_WEEK_PUBLIC_PATH,
  isFeatureOfTheWeekPublicPath,
} from "./featureOfTheWeekRoutes";

const hasRoutePermission = (
  permissionName,
  permissionChild,
  permissionAnyChildren
) => {
  if (isTrueSpOwner()) {
    return true;
  }

  if (!permissionName) {
    return true;
  }

  if (permissionAnyChildren?.length) {
    return permissionAnyChildren.some((child) =>
      getSpPermissionStatus(permissionName, child)
    );
  }

  if (
    permissionName === PERMISSION_KEYS.ROSTERING &&
    permissionChild ===
      PERMISSION_KEYS_CHILDREN.ROSTERING.TIMESHEET_EXPORT.SHOW
  ) {
    return canAccessPayrollSettings();
  }

  return getSpPermissionStatus(permissionName, permissionChild);
};

export const UseRoute = ({ setIsDisplayBanner }) => {
  const location = useLocation();
  const userType = getUserTypeFromLC();
  const permissionsVersion = usePermissionsVersion();
  const [orgAiVersion, setOrgAiVersion] = useState(0);
  useEffect(() => {
    const bump = () => setOrgAiVersion((version) => version + 1);
    window.addEventListener(SP_SETTINGS_AI_UPDATED_EVENT, bump);
    return () => window.removeEventListener(SP_SETTINGS_AI_UPDATED_EVENT, bump);
  }, []);
  const currentUser = GetItemFromLocalstorage("user");
  const currentUserFromStore = useSelector(getUser);
  const isPublicFeaturePage = isFeatureOfTheWeekPublicPath(location.pathname);
  const isOnBoarding = parseBooleanVal(localStorage.getItem("isOnBoarding"));
  const step = localStorage.getItem("step") ?? "false";
  const userForBanner = currentUserFromStore || currentUser;

  const isTwoFactorAuthenticate = () => {
    if (
      localStorage.getItem("2FASetup") &&
      localStorage.getItem("2FARequired") &&
      // localStorage.getItem("2FASetup") !== "null" &&
      localStorage.getItem("2FARequired") === "true" &&
      localStorage.getItem("step") === "false"
    ) {
      return true;
    } else if (
      !currentUser.hasOwnProperty("authentication_activity") &&
      !localStorage.getItem("authentication_uuid")
    ) {
      return true;
    }
    return false;
  };

  // Handle banner display state updates in useEffect to avoid setState during render.
  // Use userForBanner (Redux when available) so banner updates when refreshPaymentSetting runs.
  useEffect(() => {
    if (isPublicFeaturePage) {
      setIsDisplayBanner(false);
      return;
    }

    const overLimit = isOverParticipantLimit(userForBanner);
    const isSpAdmin = roleAccess([USER_ROLE.Sp_Admin]);
    const notWildcard =
      userForBanner && !parseBooleanVal(userForBanner?.payment_setting?.wildcard);

    if (step && currentUser && userType && step !== "false") {
      setIsDisplayBanner(false);
    } else if (
      userForBanner &&
      roleAccess([USER_ROLE.Sp_Standard_User]) &&
      !isOnBoarding &&
      !userForBanner.on_boarding
    ) {
      setIsDisplayBanner(false);
    } else if (userForBanner && notWildcard && isSpAdmin && (overLimit || shouldShowSubscriptionBannerForUser(userForBanner))) {
      setIsDisplayBanner(true);
    } else if (!getToken() || !currentUser || !userType) {
      setIsDisplayBanner(false);
    } else if (isTwoFactorAuthenticate() && currentUser && userType) {
      setIsDisplayBanner(true);
    }
  }, [
    step,
    currentUser,
    userType,
    isOnBoarding,
    setIsDisplayBanner,
    userForBanner,
    isPublicFeaturePage,
  ]);

  if (isPublicFeaturePage) {
    return (
      <Routes>
        <Route
          path={FEATURE_OF_THE_WEEK_PUBLIC_PATH}
          element={<FeatureOfTheWeekLanding />}
        />
      </Routes>
    );
  }

  if (step && currentUser && userType && step !== "false") {
    const routeComponents = [<Route exact path='/' element={<SignIn />} />];
    let component = null;
    switch (step) {
      case "0.5":
        routeComponents.push(
          <Route exact path='/select-otp' element={<OTPSelectionScreen />} />
        );
        component = <OTPSelectionScreen />;
        break;
      case "1":
        routeComponents.push(
          <Route exact path='/setup-otp' element={<FANotEnable />} />
        );
        component = <FANotEnable />;
        break;
      case "2":
        routeComponents.push(
          <Route path='/confirm-otp' element={<FANotEnabledOTP />} />
        );
        component = <FANotEnabledOTP />;
        break;
      case "2.2":
        routeComponents.push(
          <Route path='/verify-otp' element={<FAEnabled />} />
        );
        component = <FAEnabled />;
        break;
      case "3":
        routeComponents.push(
          <Route
            exact
            path='/verified-otp'
            element={<FANotEnabledOTPVarified />}
          />
        );
        component = <FANotEnabledOTPVarified />;
        break;
      default:
        component = null;
        break;
    }
    return (
      <Routes>
        {routeComponents}
        <Route
          path='*'
          element={component === null ? <ErrorPage2FA /> : component}
        />
      </Routes>
    );
  } else if (
    currentUser &&
    roleAccess([USER_ROLE.Sp_Standard_User]) &&
    !isOnBoarding &&
    !currentUser.on_boarding &&
    // Ghost sessions must never trap the platform admin in worker onboarding —
    // the stored user is the admin, whose record has no on_boarding flag.
    !isGhostSessionActive()
  ) {
    return (
      <Routes>
        <Route path='*' element={<OnBoardingForm />} />
      </Routes>
    );
  } else if (
    userForBanner &&
    !parseBooleanVal(userForBanner?.payment_setting?.wildcard) &&
    shouldLockToSubscriptionPlansForUser(userForBanner) &&
    roleAccess([USER_ROLE.Sp_Admin])
  ) {
    // Lock to subscription/plans only for trial expired or payment failed.
    // Participant over limit: warning banner; after 7 days API is read-only (middleware) but app routes stay available.
    return (
      <Routes>
        <Route
          path='/subscription'
          element={<SubscriptionDetailsDashboard istrailExpired={true} />}
        />
        <Route
          path={"/subscription/plans"}
          element={<PricingTable istrailExpired={true} />}
        />
        <Route
          path='/subcription/plans'
          element={<Navigate to='/subscription/plans' replace />}
        />
        <Route path='*' element={<PricingTable istrailExpired={true} />} />
      </Routes>
    );
  } else if (!getToken() || !currentUser || !userType) {
    const routeComponents = DynamicRoutes["public"].map(
      ({ path, element }, key) => (
        <Route exact path={path} element={element} key={key} />
      )
    );
    return <Routes> {routeComponents} </Routes>;
  } else if (currentUser && userType) {
    const resolvedUserType = resolveRouteUserType();

    // Authenticated: show app (whether 2FA required or not)
    const routeComponents = DynamicRoutes[resolvedUserType]
      .filter((item) => {
        // Care Watch follows the organisation's AI master switch. Hiding the
        // sidebar entry alone still leaves /care-watch reachable by typing the
        // URL, which would render an empty queue for an org without AI.
        if (item?.path === "/care-watch" && orgAiVersion >= 0 && !isCareWatchVisible()) return false;

        if (item?.isWildRoute && item?.role) {
          const isSubscriptionRoute =
            item.path === "/subscription" || item.path?.startsWith("/subscription/");
          return (
            roleAccess(item.role) &&
            (isSubscriptionRoute ||
              !parseBooleanVal(currentUser?.payment_setting?.wildcard) ||
              !!currentUser?.payment_setting?.xero_subscription_id)
          );
        } else if (item?.role) {
          return roleAccess(item.role);
        }

        return true;
      })
      .map(
        (
          {
            path,
            element,
            permissionName = null,
            permissionChild = null,
            permissionAnyChildren = null,
          },
          key
        ) => (
          <Route
            exact
            path={path}
            element={
              !hasSpAdminLevelAccess() &&
              permissionName &&
              permissionName !== null &&
              !hasRoutePermission(
                permissionName,
                permissionChild,
                permissionAnyChildren
              ) ? (
                <PermissionDenied />
              ) : (
                element
              )
            }
            key={key}
          />
        )
      );

    // Invite / set-password links must work even if an SP session is still active
    const alwaysAvailablePublic = DynamicRoutes.public.filter(({ path }) =>
      [
        "/family-set-password",
        "/reset-password",
        "/member-invitation",
        "/sp-invitation/:id",
        "/forget-password",
        "/confirmpassword",
      ].includes(path)
    );

    return (
      <Routes>
        {routeComponents}
        {alwaysAvailablePublic.map(({ path, element }, key) => (
          <Route exact path={path} element={element} key={`public-${key}`} />
        ))}
        {/* Redirect common typo so /subcription/plans still works */}
        <Route
          path='/subcription/plans'
          element={<Navigate to='/subscription/plans' replace />}
        />
        <Route path='*' element={<ErrorPage />} />
      </Routes>
    );
  } else {
    // Invalid/corrupted auth state
    localStorage.clear();
    sessionStorage.clear();
    return (
      <Routes>
        <Route path='*' element={<SignIn />} />
      </Routes>
    );
  }
};
